import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, useTheme } from '@mui/material';
import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import Iconify from 'src/components/iconify';
import { PopupContext, PopupType } from './popup-context-types';

interface Popup {
  type: PopupType;
  message: ReactNode | string | null;
  title?: string;
  cancelText?: string;
  actionText?: string;
  action?: () => void;
  cancelAction?: () => void;
}

const PopupProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { palette } = useTheme();
  const [popup, setPopup] = useState<Popup | null>(null);

  const popupContextValue = useMemo(() => ({ popup, setPopup }), [popup]);

  const title = useMemo(() => {
    if (popup?.title) return popup.title;
    switch (popup?.type) {
      case PopupType.ERROR:   return 'Error';
      case PopupType.INFO:    return 'Info';
      case PopupType.WARNING: return 'Warning';
      case PopupType.SUCCESS: return 'Success';
      default:                return '';
    }
  }, [popup?.title, popup?.type]);

  const cancelText = useMemo(
    () => popup?.cancelText ?? (popup?.action ? 'Cancel' : 'OK'),
    [popup]
  );

  const color = useMemo(() => {
    switch (popup?.type) {
      case PopupType.ERROR:   return 'error';
      case PopupType.INFO:    return 'info';
      case PopupType.WARNING: return 'warning';
      case PopupType.SUCCESS: return 'success';
      default:                return 'secondary';
    }
  }, [popup?.type]);

  const icon = useMemo(() => {
    switch (popup?.type) {
      case PopupType.ERROR:   return 'mdi:close-octagon';
      case PopupType.INFO:    return 'mdi:information';
      case PopupType.WARNING: return 'mdi:alert';
      case PopupType.SUCCESS: return 'mdi:check-circle';
      default:                return 'mdi:question-mark-circle';
    }
  }, [popup?.type]);

  const iconColor = useMemo(() => {
    switch (popup?.type) {
      case PopupType.ERROR:   return palette.error.main;
      case PopupType.INFO:    return palette.info.main;
      case PopupType.WARNING: return palette.warning.main;
      case PopupType.SUCCESS: return palette.success.main;
      default:                return palette.grey[500];
    }
  }, [palette, popup?.type]);

  const handleCancelClick = useCallback(() => {
    setPopup(null);
    if (popup?.cancelAction) popup.cancelAction();
  }, [popup]);

  const handleActionClick = useCallback(() => {
    setPopup(null);
    if (popup?.action) popup.action();
  }, [popup]);

  const message = useMemo((): ReactNode | string => popup?.message ?? '', [popup?.message]);

  return (
    <PopupContext.Provider value={popupContextValue}>
      {children}
      <Dialog open={!!popup} onClose={handleCancelClick} fullWidth>
        <DialogTitle sx={{ pb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
            <Iconify icon={icon} color={iconColor} width={24} />&nbsp;&nbsp;
            <span style={{ fontSize: '20px', lineHeight: '20px' }}>{title}</span>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <DialogContentText>{message}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ pt: 2 }}>
          <Button variant="outlined" color="inherit" onClick={handleCancelClick}>{cancelText}</Button>
          {popup?.action && (
            <Button variant="contained" color={color} onClick={handleActionClick}>
              {popup?.actionText ?? 'OK'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </PopupContext.Provider>
  );
};

export default PopupProvider;
